import React, { useState, useCallback } from 'react';
import ListFilterProps from './props';
import './style.scss';
import Collapsible from '../../private/Collapsible/Collapsible';
import Input from '../../private/Input/Input';
import input from '../../private/Input/props';

function ListFilter(props: ListFilterProps) {
  const {
    collapsed = false,
    e2e,
    inputType,
    items,
    maxItems,
    searchEnabled,
    searchPlaceholder,
    showMore,
    title,
  } = props;

  // Track search terms for each level
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Track which levels are showing all items (beyond max limit)
  const [showAllItems, setShowAllItems] = useState<boolean>(false);
  // Track the status of the collapsible component
  const [isOpen, setIsOpen] = useState<boolean>(!collapsed);

  // Filter data based on search term
  const getFilteredDataForLevel = useCallback((items: input[]): input[] => {
    if (!searchTerm) {
      return items;
    }

    return items.filter(item => 
      item.Label?.text?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  // Handle level expansion/collapse
  const handleOpenToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  // Handle search input change
  const handleSearchChange = useCallback((searchTerm: string) => {
    setSearchTerm(searchTerm);
  }, []);

  // Handle show more button click
  const handleShowMore = useCallback(() => {
    setShowAllItems(!showAllItems);
  }, [showAllItems]);

  // Get items to display for a level (respecting max items limit)
  const getDisplayItems = useCallback((filteredData: input[], maxItems: number): { items: input[], hasMore: boolean } => {
    if (!filteredData || showAllItems || filteredData.length <= maxItems) {
      return { items: filteredData, hasMore: false };
    }
    
    return { 
      items: filteredData.slice(0, maxItems), 
      hasMore: true 
    };
  }, [showAllItems]);

  // Render a single level
  const renderList = useCallback((items: input[], inputType: 'radio' | 'checkbox' = 'radio', maxItems = 10, searchEnabled = false, searchPlaceholder = '', title: string) => {
    const filteredData = getFilteredDataForLevel(items);
    const { items: items2Display, hasMore } = getDisplayItems(filteredData, maxItems);

    const inputs: input[] = [];
    // Search input
    let searchInput = null;
    if (searchEnabled) {
      searchInput = <Input
        id={`list-filter-search-input-id-${title.toLowerCase()}`}
        type="text"
        placeholder={ searchPlaceholder || `Search ${title.toLowerCase()}...` }
        value={ searchTerm || '' }
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
        Label={{
          text: `Search ${title}`,
          hidden: true
        }}
        classNames={{
          search: true,
        }}
      />;
    }
    // Options
    inputs.push(...items2Display.map(item => ({
      id: item.id,
      name: item.name,
      defaultChecked: item.defaultChecked || false,
      disabled: item.disabled,
      Label: item.Label,
      Link: item.Link,
      classNames: {
        checkbox: inputType === 'checkbox',
        radio: inputType === 'radio',
        labelVisuallyHidden: true,
        link: true
      },
    })));

    return (
      <div>
        <Collapsible
          header={title}
          isOpen={isOpen}
          onOpen={() => handleOpenToggle()}
          classNames={{
            section: true,
          }}
          // children={searchEnabled ? searchInput : null}
          InputGroup={{
            items: inputs.map(input => ({
              Input: input
            })),
            classNames: {
              filter: true,
              background: true,
            }
          }}
          List={{
            items: hasMore ? [{
              Link: {
                text: showMore?.text || 'Show more...',
                classNames: {
                  medium: true
                },
                url: showMore?.url || '#',
                hyperlinkAttributes: {
                  rel: 'nofollow',
                  target: '_blank'
                },
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleShowMore();
                }
              }
            }] : []
          }}
        >
          { searchInput }
        </Collapsible>
      </div>
    );
  }, [
    getFilteredDataForLevel,
    getDisplayItems,
    searchTerm,
    handleOpenToggle,
    handleSearchChange,
    handleShowMore,
    isOpen,
    showMore
  ]);

  return (
    <div
      className="list-filter"
      data-e2e={e2e}
    >
      {renderList(items, inputType, maxItems, searchEnabled, searchPlaceholder, title)}
    </div>
  );
}

export default React.memo(ListFilter);
